import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../store/hooks/useAuth';

function LoginPage() {
  const { user, loading, error, isAuthenticated, awaitingOtp, otpEmail, login, logout, verifyOtp, backToLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleLogin = (e) => {
    e.preventDefault();
    login({ email, password });
    setPassword('');
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const result = await verifyOtp({ otp, email: otpEmail ?? email });
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success('Welcome! You have successfully signed in.');
    }
    setOtp('');
  };

  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
        <div className="card-premium w-full max-w-md p-8 shadow-premium-xl">
          <h2 className="font-display font-semibold text-2xl text-slate-800 tracking-tight">Welcome</h2>
          <div className="mt-6 space-y-3 text-slate-600">
            <p className="flex justify-between"><span className="text-slate-500">Name:</span> <span className="font-medium text-slate-800">{user?.name}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Email:</span> <span className="font-medium text-slate-800">{user?.email}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Role:</span> <span className="font-medium text-slate-800">{user?.role || 'Admin'}</span></p>
          </div>
          <button
            onClick={logout}
            disabled={loading}
            className="mt-8 w-full btn-primary py-3"
          >
            {loading ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-60" />
        <div className="relative flex flex-col justify-center px-16 xl:px-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <span className="font-display font-bold text-2xl text-white tracking-tight">Groom</span>
          </div>
          <h1 className="font-display font-bold text-4xl xl:text-5xl text-white tracking-tight leading-tight">
            Premium management for your salon business
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-md">
            Streamline bookings, services, and staff. Everything you need in one elegant dashboard.
          </p>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-slate-50 via-white to-amber-50/20">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            </div>
            <span className="font-display font-bold text-xl text-slate-800 tracking-tight">Salon Admin</span>
          </div>

          <div className="card-premium p-8 shadow-premium-xl">
            {awaitingOtp ? (
              <>
                <h2 className="font-display font-semibold text-2xl text-slate-800 tracking-tight">Verify OTP</h2>
                <p className="mt-2 text-slate-500 text-sm">Enter the OTP sent to {otpEmail ?? email}</p>

                <form onSubmit={handleVerifyOtp} className="mt-8 space-y-5">
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-slate-700 mb-2">OTP Code</label>
                    <input
                      id="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 6-digit OTP"
                      className="input-premium"
                      required
                      disabled={loading}
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                  </div>

                  <button type="submit" disabled={loading} className="w-full btn-primary py-3 mt-2">
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                  <button
                    type="button"
                    onClick={backToLogin}
                    disabled={loading}
                    className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
                  >
                    Back to login
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="font-display font-semibold text-2xl text-slate-800 tracking-tight">Admin Login</h2>
                <p className="mt-2 text-slate-500 text-sm">Sign in to access your dashboard</p>

                <form onSubmit={handleLogin} className="mt-8 space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@salon.com"
                      className="input-premium"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="input-premium"
                      required
                      disabled={loading}
                    />
                  </div>

                  <button type="submit" disabled={loading} className="w-full btn-primary py-3 mt-2">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
